import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchPublicMediaReports } from "../../../api/dripPlan";
import type { MediaReportItem } from "../../../api/dripPlan";

function formatDate(dateStr: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export default function MediaReports() {
  const [reports, setReports] = useState<MediaReportItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchPublicMediaReports()
      .then((list) => {
        if (!cancelled) setReports(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setReports([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="py-12 md:py-20 px-4 md:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-2xl md:text-4xl font-bold text-[#1E1E1E] mb-3 md:mb-4">媒体报道</h2>
          <p className="text-sm md:text-lg text-[#6C6C6C]">主流媒体持续关注，传播公益正能量</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#6C6C6C]">加载中...</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 text-[#6C6C6C]">暂无媒体报道</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-6">
            {reports.map((report) => (
              <Link
                key={report.id}
                to={`/media/${report.id}`}
                className="bg-[#F9F9F9] rounded-lg md:rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="p-2.5 md:p-6">
                  <div className="flex items-center gap-1.5 md:gap-3 mb-2 md:mb-4">
                    {report.sourceLogo ? (
                      <img
                        src={report.sourceLogo}
                        alt={report.sourceName}
                        className="w-6 h-6 md:w-12 md:h-12 rounded-md md:rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 md:w-12 md:h-12 rounded-md md:rounded-lg bg-[#E0E0E0] flex items-center justify-center text-[#6C6C6C] text-xs font-medium">
                        {report.sourceName?.slice(0, 1) || "媒"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[9px] md:text-sm font-bold text-[#2A7A62] truncate">
                        {report.sourceName || "媒体报道"}
                      </h3>
                      <p className="text-[7px] md:text-xs text-[#6C6C6C]">
                        {formatDate(report.publicationDate)}
                      </p>
                    </div>
                  </div>

                  <h4 className="text-[10px] md:text-base font-bold text-[#1E1E1E] mb-1 md:mb-2 line-clamp-2 group-hover:text-[#2A7A62] transition-colors">
                    {report.title}
                  </h4>

                  <p className="text-[8px] md:text-sm text-[#6C6C6C] line-clamp-2 md:line-clamp-3 mb-1.5 md:mb-3">
                    {report.summary || ""}
                  </p>

                  <div className="flex items-center text-[#2A7A62] text-[8px] md:text-sm font-semibold group-hover:gap-2 transition-all">
                    <span>阅读全文</span>
                    <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
